import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { MatSort, MatSortModule } from '@angular/material/sort';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { NaturezaMovimentacaoEnum } from '../../../shared/enums/financas/natureza-movimentacao.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MaskService } from '../../../shared/services/mask.service';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';
import { LimitDateValidator } from '../../../shared/validators/limit-date.validator';
import { ConciliacaoCartaoCreditoService } from '../../../shared/services/apis/financas/conciliacao-cartao-credito.service';
import { ExtratoCartaoConciliadoItem, VinculoConciliadoCartao } from '../../../shared/services/responses/financas/get-lancamentos-conciliados-cartao.response';
import { DesconciliarCartaoDialogComponent } from './dialogs/desconciliar-cartao-dialog/desconciliar-cartao-dialog.component';

export enum PeriodoConciliadosEnum {
    Hoje = 1,
    DaSemana = 2,
    DoMes = 3,
    Ultimos30Dias = 4,
    DoIntervalo = 5,
}

export interface CartaoConciliadoDisplayRow {
    data: ExtratoCartaoConciliadoItem;
    isChild: boolean;
    vinculo?: VinculoConciliadoCartao;
}

@Component({
    selector: 'app-conciliacao-cartao-conciliados',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        RouterModule,
        HeaderComponent,
        EmptyListComponent,
        LoadingBoxDefaultListComponent,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatTooltipModule,
        MatMenuModule,
        MatDialogModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSortModule,
        AbntPipe,
    ],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
    ],
    templateUrl: './conciliacao-cartao-conciliados.component.html',
    styleUrls: ['./conciliacao-cartao-conciliados.component.css'],
})
export class ConciliacaoCartaoConciliadosComponent implements OnInit {
    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_ConciliacaoBancaria;
    public naturezaEnum = NaturezaMovimentacaoEnum;
    public PeriodoConciliadosEnum = PeriodoConciliadosEnum;

    public idContaFinanceira!: number;
    public isLoading = false;
    public _temMais = false;
    public _paginaAtual = 0;
    public totalConciliado = 0;
    public pesquisa = '';

    public model!: FormGroup;
    public periodoSelecionado: PeriodoConciliadosEnum = PeriodoConciliadosEnum.DoMes;
    public limiteMaxDias = 365;
    private periodoSelecionadoAplicado: PeriodoConciliadosEnum = PeriodoConciliadosEnum.DoMes;
    private periodoIntervaloDeAplicado: Date | null = null;
    private periodoIntervaloAteAplicado: Date | null = null;
    private periodoMenuFoiAplicado = false;

    @ViewChild('periodoMenuTrigger') periodoMenuTrigger?: MatMenuTrigger;

    public displayedColumns: string[] = ['data', 'descricao', 'tipo', 'valor', 'actions'];
    public _dataSource = new MatTableDataSource<ExtratoCartaoConciliadoItem>([]);
    public _flatDataSource = new MatTableDataSource<CartaoConciliadoDisplayRow>([]);
    public expandedElement: ExtratoCartaoConciliadoItem | null = null;

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._flatDataSource.sort = sort;
        this._flatDataSource.sortingDataAccessor = (item, property) => {
            if (item.isChild) return normalizarValorOrdenacao('');
            switch (property) {
                case 'data': return normalizarValorOrdenacao(item.data.dataTransacao);
                case 'descricao': return normalizarValorOrdenacao(item.data.memo ?? '');
                case 'valor': return normalizarValorOrdenacao(item.data.valor);
                default: return normalizarValorOrdenacao((item as any)[property]);
            }
        };
    }

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private dialog: MatDialog,
        private alertService: AlertService,
        private cartaoService: ConciliacaoCartaoCreditoService,
    ) { }

    ngOnInit(): void {
        this.idContaFinanceira = Number(this.route.snapshot.paramMap.get('idContaFinanceira'));

        const hoje = new Date();
        const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

        this.model = this.fb.group({
            dataDe: [primeiroDia, Validators.required],
            dataAte: [hoje, Validators.required],
        }, { validators: LimitDateValidator.rangeLimit(2) });

        this.salvarPeriodoAplicado();
        this.carregarConciliados(true);
    }

    public carregarConciliados(resetar = true): void {
        if (!this.idContaFinanceira || this.isLoading) return;
        if (resetar) {
            this._paginaAtual = 0;
            this.expandedElement = null;
        }
        this.isLoading = true;

        const formValue = this.model.getRawValue();
        const dataInicio = formValue.dataDe ? this.formatDate(new Date(formValue.dataDe)) : null;
        const dataFim = formValue.dataAte ? this.formatDate(new Date(formValue.dataAte)) : null;

        this.cartaoService.getLancamentosConciliados({
            idContaFinanceira: this.idContaFinanceira,
            dataInicio,
            dataFim,
            pesquisa: this.pesquisa.trim() || null,
            pageIndex: this._paginaAtual,
        }).subscribe({
            next: res => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao carregar conciliados.', AlertType.error);
                    return;
                }
                this._temMais = res.temMais;
                if (resetar) {
                    this._dataSource.data = res.itens;
                    this.totalConciliado = res.itens.reduce((acc, i) => acc + Math.abs(i.valor), 0);
                } else {
                    this._dataSource.data = [...this._dataSource.data, ...res.itens];
                    this.totalConciliado += res.itens.reduce((acc, i) => acc + Math.abs(i.valor), 0);
                }
                this.reconstruirFlatDataSource();
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Erro ao comunicar com o servidor.', AlertType.error);
            },
        });
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarConciliados(false);
    }

    public toggleRow(row: CartaoConciliadoDisplayRow): void {
        if (row.isChild) return;
        this.expandedElement = this.expandedElement === row.data ? null : row.data;
        this.reconstruirFlatDataSource();
    }

    public reconstruirFlatDataSource(): void {
        const flat: CartaoConciliadoDisplayRow[] = [];
        for (const item of this._dataSource.data) {
            flat.push({ data: item, isChild: false });
            if (this.expandedElement === item) {
                for (const vinculo of item.vinculos) {
                    flat.push({ data: item, isChild: true, vinculo });
                }
            }
        }
        this._flatDataSource.data = flat;
    }

    public labelTipoVinculo(vinculo: VinculoConciliadoCartao): string {
        if (vinculo.idContasPagarParcela) return 'Conta a Pagar';
        return 'Movimentação';
    }

    public classeTipoVinculo(vinculo: VinculoConciliadoCartao): string {
        if (vinculo.idContasPagarParcela) return 'badge-amarelo';
        return 'badge-azul';
    }

    public abrirDesconciliar(item: ExtratoCartaoConciliadoItem): void {
        const ref = this.dialog.open(DesconciliarCartaoDialogComponent, {
            data: { extrato: item },
            disableClose: false,
        });
        ref.afterClosed().subscribe(result => {
            if (result?.sucesso) {
                this.alertService.show('Sucesso', 'Conciliação revertida.', AlertType.success);
                this.carregarConciliados(true);
            }
        });
    }

    public voltar(): void {
        this.router.navigate(['/financas/conciliacao-bancaria/cartao/lancamentos', this.idContaFinanceira]);
    }

    public onDateInput(event: Event): void {
        MaskService.onDateInput(event);
    }

    // ── Período ──────────────────────────────────────────────────────────────

    get periodoLabel(): string {
        switch (this.periodoSelecionado) {
            case PeriodoConciliadosEnum.Hoje: return 'hoje';
            case PeriodoConciliadosEnum.DaSemana: return 'desta semana';
            case PeriodoConciliadosEnum.DoMes: return 'do mês';
            case PeriodoConciliadosEnum.Ultimos30Dias: return 'últimos 30 dias';
            case PeriodoConciliadosEnum.DoIntervalo: return 'do intervalo';
            default: return 'do mês';
        }
    }

    public selecionarPeriodo(periodo: PeriodoConciliadosEnum): void {
        this.periodoSelecionado = periodo;
        if (periodo !== PeriodoConciliadosEnum.DoIntervalo)
            this.calcularDatasParaPeriodo(periodo);
    }

    private calcularDatasParaPeriodo(periodo: PeriodoConciliadosEnum): void {
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        let dataDe: Date;
        let dataAte: Date;

        switch (periodo) {
            case PeriodoConciliadosEnum.Hoje:
                dataDe = new Date(hoje);
                dataAte = new Date(hoje);
                break;
            case PeriodoConciliadosEnum.DaSemana: {
                dataDe = new Date(hoje);
                dataDe.setDate(hoje.getDate() - hoje.getDay());
                dataAte = new Date(dataDe);
                dataAte.setDate(dataDe.getDate() + 6);
                break;
            }
            case PeriodoConciliadosEnum.DoMes: {
                dataDe = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
                dataAte = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
                break;
            }
            case PeriodoConciliadosEnum.Ultimos30Dias:
            default: {
                dataDe = new Date(hoje);
                dataDe.setDate(hoje.getDate() - 30);
                dataAte = new Date(hoje);
                break;
            }
        }

        this.model.patchValue({ dataDe, dataAte });
    }

    public aplicarPeriodo(trigger?: MatMenuTrigger): void {
        if (this.periodoSelecionado === PeriodoConciliadosEnum.DoIntervalo) {
            this.model.get('dataDe')?.markAsTouched();
            this.model.get('dataAte')?.markAsTouched();

            if (this.model.get('dataAte')?.hasError('dataInvalida')) {
                this.alertService.show('Atenção', 'A data final não pode ser menor que a data inicial.', AlertType.warning);
                return;
            }
            if (this.model.get('dataAte')?.hasError('intervaloExcedido')) {
                this.alertService.show('Atenção', `O intervalo máximo permitido é de ${this.limiteMaxDias} dias.`, AlertType.warning);
                return;
            }
            if (this.model.get('dataDe')?.hasError('required') || this.model.get('dataAte')?.hasError('required')) {
                this.alertService.show('Atenção', 'Preencha as duas datas do intervalo.', AlertType.warning);
                return;
            }
        }

        this.periodoMenuFoiAplicado = true;
        this.salvarPeriodoAplicado();
        this.carregarConciliados(true);
        const menuTrigger = trigger ?? this.periodoMenuTrigger;
        setTimeout(() => menuTrigger?.closeMenu());
    }

    public prepararPeriodoIntervalo(): void {
        this.periodoMenuFoiAplicado = false;
    }

    public cancelarPeriodoIntervalo(trigger?: MatMenuTrigger): void {
        this.restaurarPeriodoAplicado();
        this.periodoMenuFoiAplicado = true;
        const menuTrigger = trigger ?? this.periodoMenuTrigger;
        setTimeout(() => menuTrigger?.closeMenu());
    }

    public onPeriodoMenuClosed(): void {
        if (!this.periodoMenuFoiAplicado)
            this.restaurarPeriodoAplicado();
    }

    private salvarPeriodoAplicado(): void {
        this.periodoSelecionadoAplicado = this.periodoSelecionado;
        this.periodoIntervaloDeAplicado = this.cloneDate(this.model.get('dataDe')?.value);
        this.periodoIntervaloAteAplicado = this.cloneDate(this.model.get('dataAte')?.value);
    }

    private restaurarPeriodoAplicado(): void {
        this.periodoSelecionado = this.periodoSelecionadoAplicado;
        this.model.patchValue({
            dataDe: this.cloneDate(this.periodoIntervaloDeAplicado),
            dataAte: this.cloneDate(this.periodoIntervaloAteAplicado),
        });
    }

    private cloneDate(data: Date | null): Date | null {
        return data ? new Date(data) : null;
    }

    private formatDate(date: Date): string {
        return date.toISOString().split('T')[0];
    }
}
